import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useResource } from "@/hooks/useResource";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, ErrorState } from "@/components/common/States";
import { RecipeEditor } from "@/components/common/RecipeEditor";
import { RecipeCostSummary } from "@/components/recipes/RecipeCostSummary";
import { IngredientCostTable } from "@/components/recipes/IngredientCostTable";
import { CostBridge } from "@/components/recipes/CostBridge";
import { SkuCostingSection } from "@/components/recipes/SkuCostingSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtDateTime, titleCase } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft, Package, Tag, Send, Check, X, GitBranch, Loader2, Pencil,
  IndianRupee, History, Trash2,
} from "lucide-react";

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

function Metric({ label, value, tone = "text-stone-900", testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function RecipeReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canApprove = ["super_admin", "management", "qa_manager", "production_manager"].includes(role);
  const canSetDecidedCost = ["super_admin", "management", "accounts"].includes(role);

  const [recipe, setRecipe] = useState(null);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [actionBusy, setActionBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [dcOpen, setDcOpen] = useState(false);
  const [dcCost, setDcCost] = useState("");
  const [dcReason, setDcReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState(null);

  const load = async () => {
    setLoadState({ loading: true, error: null });
    try {
      const res = await api.get(`/recipes/${id}`);
      setRecipe(res.data);
      setLoadState({ loading: false, error: null });
      api.get(`/recipes/${id}/costing-review`).then((x) => { setReview(x.data); setReviewError(null); })
        .catch((e) => setReviewError(apiErrorMessage(e, "Could not calculate costing")));
    } catch (e) {
      setLoadState({ loading: false, error: apiErrorMessage(e, "Could not load recipe") });
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["resource", "/recipes"] });

  const r = recipe || {};
  const changeLog = Array.isArray(r.changeLog) ? r.changeLog : [];
  const status = String(r.status || "").toLowerCase();
  const isDraft = status === "draft" || status === "rejected";
  const isPending = status === "pending" || status === "pending_approval" || status === "submitted";
  const isApproved = status === "active" || status === "approved";

  // Cost governance — the recipe carries only its LOCKED decided/standard cost.
  // Ingredient price movements never create a recipe variance; actual cost variance
  // is computed and displayed at the PRODUCTION BATCH level instead.
  const decided = num(r.decidedCost);

  const runAction = async (path, body) => {
    setActionBusy(true);
    try {
      const res = await api.post(`/recipes/${r.id}${path}`, body || undefined);
      toast.success("Done");
      if (path === "/new-version" && res.data?.id) {
        invalidate();
        navigate(`/recipes/${res.data.id}`);
        return;
      }
      invalidate();
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Action failed"));
    } finally {
      setActionBusy(false);
    }
  };

  const saveDecidedCost = async () => {
    setDcOpen(false);
    setActionBusy(true);
    try {
      await api.post(`/recipes/${r.id}/decided-cost`, { decidedCost: Number(dcCost) || 0, reason: dcReason.trim() });
      toast.success("Decided cost set");
      setDcCost(""); setDcReason("");
      invalidate(); load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not set decided cost"));
    } finally { setActionBusy(false); }
  };

  const doDelete = async () => {
    setDeleteOpen(false);
    setActionBusy(true);
    try {
      await api.delete(`/recipes/${r.id}`);
      toast.success("Recipe deleted");
      invalidate();
      navigate("/recipes");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setActionBusy(false); }
  };

  const handleSave = async (payload, action) => {
    setSubmitting(true);
    try {
      await api.put(`/recipes/${r.id}`, payload);
      if (action === "submit") {
        await api.post(`/recipes/${r.id}/submit`, undefined);
        toast.success("Recipe submitted for approval");
      } else {
        toast.success("Recipe draft updated");
      }
      setEditorOpen(false);
      invalidate(); load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSubmitting(false); }
  };

  if (loadState.loading) return <div className="p-6"><LoadingState label="Loading recipe…" /></div>;
  if (loadState.error) return <div className="p-6"><ErrorState message={loadState.error} onRetry={load} /></div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="recipe-review-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 border-b border-stone-200 bg-stone-50/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3.5">
          <div className="flex items-center gap-3">
            <button data-testid="recipe-review-back" onClick={() => navigate("/recipes")}
              className="rounded-lg p-1.5 text-stone-500 hover:bg-stone-200/60 hover:text-stone-800">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-display text-lg font-bold text-stone-900">RECIPE REVIEW</h1>
              <p className="text-xs text-stone-500">{r.productName} · Recipe V{r.version}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {r.approvedBy && <span className="hidden text-xs text-stone-400 sm:block">Approved by {r.approvedBy}</span>}
            <StatusBadge value={r.status} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-5 px-6 py-6">
        {/* Workflow action bar */}
        <div className="flex flex-wrap items-center gap-2" data-testid="recipe-review-actions">
          {isDraft && (
            <Button data-testid="recipe-action-edit" size="sm" variant="outline" className="gap-1.5" disabled={actionBusy} onClick={() => setEditorOpen(true)}>
              <Pencil className="h-3.5 w-3.5" /> Edit Draft
            </Button>
          )}
          {isDraft && (
            <Button data-testid="recipe-action-submit" size="sm" variant="outline" className="gap-1.5" disabled={actionBusy} onClick={() => runAction("/submit")}>
              <Send className="h-3.5 w-3.5" /> Submit
            </Button>
          )}
          {isPending && canApprove && (
            <>
              <Button data-testid="recipe-action-approve" size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={actionBusy} onClick={() => runAction("/approve")}>
                <Check className="h-3.5 w-3.5" /> Approve
              </Button>
              <Button data-testid="recipe-action-reject" size="sm" variant="outline" className="gap-1.5 text-rose-600" disabled={actionBusy} onClick={() => setRejectOpen(true)}>
                <X className="h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {isApproved && (
            <Button data-testid="recipe-action-new-version" size="sm" variant="outline" className="gap-1.5" disabled={actionBusy} onClick={() => runAction("/new-version")}>
              <GitBranch className="h-3.5 w-3.5" /> New Version
            </Button>
          )}
          {canApprove && (
            <Button data-testid="recipe-action-delete" size="sm" variant="outline" className="gap-1.5 text-rose-600" disabled={actionBusy} onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          )}
          {actionBusy && <Loader2 className="h-4 w-4 animate-spin text-amber-700" />}
        </div>

        {/* Linked product */}
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Package className="h-4 w-4 text-emerald-700" />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Linked Product</div>
              <div className="font-mono text-sm font-semibold text-stone-900">{r.productId} · {r.productName}</div>
            </div>
          </div>
          {r.sku && <span className="font-mono text-sm font-semibold text-stone-700">{r.sku}</span>}
        </div>

        {/* Cost governance */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="recipe-cost-governance">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-stone-800">Decided / Standard Cost (₹ / Unit)</h3>
          </div>
          <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-2">
            <Metric label="Decided / Standard Cost" value={fmtCurrency(decided)} testId="recipe-decided-cost" />
            <Metric label="Cost per Finished Unit" value={fmtCurrency(r.costSnapshot?.unitCost ?? decided)} testId="recipe-standard-unit-cost" />
          </div>
          <p className="mt-3 text-xs text-stone-500">
            This is the locked standard cost of the approved formulation. Ingredient price changes never alter it and never
            create versions or variance warnings — actual cost variance is measured on each Production Batch instead.
          </p>
          {canSetDecidedCost && (
            <Button data-testid="recipe-set-decided-cost" variant="outline"
              className="mt-3 w-full gap-2 border-stone-300 font-semibold uppercase tracking-wide"
              onClick={() => { setDcCost(String(r.decidedCost ?? r.currentUnitCost ?? "")); setDcReason(""); setDcOpen(true); }}>
              <Tag className="h-4 w-4" /> Set / Change Decided Cost
            </Button>
          )}
        </div>

        {review ? (
          <>
            <RecipeCostSummary data={review} />
            <IngredientCostTable data={review} />
            <CostBridge data={review} />
            <SkuCostingSection data={review} />
          </>
        ) : reviewError ? <ErrorState message={reviewError} onRetry={load} /> : <LoadingState label="Calculating costing…" />}

        {/* Change log */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="recipe-change-log">
          <div className="mb-3 flex items-center gap-2">
            <History className="h-4 w-4 text-stone-500" />
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-stone-800">Change Log</h3>
          </div>
          {changeLog.length === 0 ? <p className="text-sm text-muted-foreground">No change log entries.</p> : (
            <div className="space-y-2">
              {changeLog.map((c, i) => (
                <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-stone-100 bg-stone-50/60 px-3 py-2">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-stone-800">{c.action || c.event || titleCase(Object.keys(c)[0] || "change")}</span>
                    {c.note && <div className="text-xs text-stone-500">{c.note}</div>}
                  </div>
                  <span className="shrink-0 text-right text-[11px] text-stone-400">
                    {c.by && <div>{c.by}</div>}
                    {c.at && <div>{fmtDateTime(c.at)}</div>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <RecipeEditor open={editorOpen} onOpenChange={setEditorOpen} mode="edit" initialValues={r} onSubmit={handleSave} submitting={submitting} />

      {/* Reject */}
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject recipe</AlertDialogTitle>
            <AlertDialogDescription>Provide a reason. This updates the recipe status in the live ERP.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection… (required)" className="min-h-[80px]" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="reject-confirm" disabled={!rejectReason.trim()} className="bg-rose-600 hover:bg-rose-700"
              onClick={() => { setRejectOpen(false); runAction("/reject", { reason: rejectReason.trim() }); setRejectReason(""); }}>
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Decided cost */}
      <AlertDialog open={dcOpen} onOpenChange={setDcOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><IndianRupee className="h-5 w-5 text-amber-600" /> Set Decided Cost</AlertDialogTitle>
            <AlertDialogDescription>
              Sets the managed "decided" unit cost for this recipe. A price change does NOT create a new recipe version — only a formulation change does.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Decided Unit Cost (₹)</Label>
              <Input data-testid="recipe-dc-cost" type="number" value={dcCost} onChange={(e) => setDcCost(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reason</Label>
              <Textarea data-testid="recipe-dc-reason" value={dcReason} onChange={(e) => setDcReason(e.target.value)} placeholder="Why is the decided cost being set/changed?" className="min-h-[70px] text-sm" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recipe-dc-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="recipe-dc-save" disabled={!dcCost || !dcReason.trim()} className="bg-amber-700 hover:bg-amber-800" onClick={saveDecidedCost}>Save decided cost</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe version?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {r.recipeNumber} V{r.version} ({r.productName}).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="recipe-delete-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
